import React, { useEffect, useState } from 'react';

import { getApiBase } from '../services/apiBase';
import { Language, t } from '../data/translations';

import scrollRolledPng from '../assets/custom/scroll_set/scroll_rolled_green.png';
import scrollPaperPng from '../assets/custom/scroll_set/scroll_paper_green.png';
import trumpetPng from '../assets/custom/scroll_set/trumpet_green.png';

const SCROLL_ROLLED = scrollRolledPng;
const SCROLL_PAPER = scrollPaperPng;
const TRUMPET = trumpetPng;

interface LeaderboardEntry {
    username: string;
    fame: number;
    hero?: string;
    faction?: string;
    opalstoneId?: string;
}

interface ScrollLeaderboardProps {
    language: Language;
    title?: string;
    endpoint?: string;
    variant?: 'default' | 'compact';
}

const ScrollLeaderboard: React.FC<ScrollLeaderboardProps> = ({ language, title, endpoint, variant = 'default' }) => {
    const [data, setData] = useState<LeaderboardEntry[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const isCompact = variant === 'compact';

    useEffect(() => {
        const base = getApiBase();
        let isMounted = true;
        setLoading(true);
        setIsOpen(false);

        fetch(`${base}${endpoint || '/api/fame-leaderboard'}`)
            .then((r) => r.json())
            .then((res) => {
                if (isMounted && res.ok && Array.isArray(res.top)) {
                    setData(res.top);
                }
            })
            .catch(console.error)
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        const openTimer = window.setTimeout(() => {
            if (isMounted) {
                setIsOpen(true);
            }
        }, 300);

        return () => {
            isMounted = false;
            window.clearTimeout(openTimer);
        };
    }, [endpoint]);

    const scrollWidth = isCompact ? 'min(100%, 300px)' : 320;
    const trumpetSize = isCompact ? 'w-20 h-16' : 'w-32 h-24';
    const bodyHeightClass = isCompact ? 'h-[188px]' : 'h-[400px]';
    const topBottomHeightClass = isCompact ? 'h-[44px]' : 'h-[70px]';
    const titleClass = isCompact ? 'text-base' : 'text-xl';
    const contentPaddingClass = isCompact ? 'px-3 py-4' : 'px-4 py-8';

    return (
        <div className="relative flex flex-col items-center" style={{ width: scrollWidth }}>
            <div className={`absolute ${isCompact ? 'top-[-32px]' : 'top-[-48px]'} w-full flex justify-center items-end pointer-events-none transition-all duration-1000 ease-out z-30 ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div className={`relative ${trumpetSize} flex justify-center items-center`}>
                    <img
                        src={TRUMPET}
                        className="absolute w-full h-full object-contain transform -scale-x-100 -rotate-12"
                        alt="Trumpet Left"
                        style={{ right: 'calc(42% - 30px)' }}
                    />
                    <img
                        src={TRUMPET}
                        className="absolute w-full h-full object-contain transform rotate-12"
                        alt="Trumpet Right"
                        style={{ left: 'calc(42% - 30px)' }}
                    />
                </div>
            </div>

            <div className={`z-20 w-[115%] ${topBottomHeightClass} relative -mb-[15px] transform scale-x-110 translate-y-[15px]`}>
                <img
                    src={SCROLL_ROLLED}
                    className="w-full h-full object-contain drop-shadow-xl"
                    alt="Scroll Top"
                />
            </div>

            <div
                className={`relative w-[85%] overflow-hidden transition-all duration-[1500ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] ${isOpen ? bodyHeightClass : 'h-0'}`}
                style={{ zIndex: 10 }}
            >
                <div className="absolute inset-0 w-full h-full">
                    <img
                        src={SCROLL_PAPER}
                        className="w-full h-full object-cover opacity-90"
                        alt="Scroll Paper"
                    />
                </div>

                <div className={`absolute inset-0 w-full h-full overflow-y-auto scrollbar-thin scrollbar-thumb-amber-700 scrollbar-track-transparent ${contentPaddingClass}`}>
                    <h3 className={`text-center font-serif text-amber-900 ${titleClass} font-bold mb-4 uppercase tracking-widest border-b-2 border-amber-900/30 pb-2`}>
                        {title || t('ui.mostWanted', language)}
                    </h3>

                    {loading && <div className="text-center text-amber-800 animate-pulse">Summoning Scribes...</div>}

                    {!loading && data.length === 0 && (
                        <div className="text-center text-amber-800 italic">The Hall of Fame is empty.</div>
                    )}

                    <div className="space-y-2 pb-6">
                        {data.map((player, idx) => (
                            <div key={player.username} className="flex items-center text-amber-950 font-serif text-sm border-b border-amber-900/10 pb-1">
                                <div className={`w-8 font-bold ${idx < 3 ? 'text-amber-700 text-lg' : 'text-amber-900/60'}`}>
                                    #{idx + 1}
                                </div>
                                <div className="flex-1 truncate font-semibold">
                                    {player.username}
                                </div>
                                <div className="font-mono font-bold text-amber-800">
                                    {player.fame}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className={`z-20 w-[115%] ${topBottomHeightClass} relative -mt-[15px] transform scale-x-110`}>
                <img
                    src={SCROLL_ROLLED}
                    className="w-full h-full object-contain drop-shadow-xl"
                    alt="Scroll Bottom"
                />
            </div>

            <style>{`
        .scrollbar-thin::-webkit-scrollbar { width: 6px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background-color: rgba(146, 64, 14, 0.5); border-radius: 3px; }
      `}</style>
        </div>
    );
};

export default ScrollLeaderboard;
